import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ShieldAlert, Trophy } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, API } from "../App";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const { user, token, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const mustChange = !!user?.must_change_password;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/auth/change-password`,
        mustChange
          ? { new_password: newPassword }
          : { current_password: currentPassword, new_password: newPassword },
        { headers }
      );
      // Clear must_change flag client-side & continue
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated successfully!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-8">
          <Trophy className="w-8 h-8 text-[#FFD700]" />
          <span className="font-['Playfair_Display'] text-xl font-bold text-white">JackAndJill</span>
        </div>

        {mustChange && (
          <div className="mb-6 p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex gap-3" data-testid="must-change-banner">
            <ShieldAlert className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
            <div>
              <p className="text-[#F59E0B] font-medium text-sm">Please change your password</p>
              <p className="text-[#A1A1AA] text-xs mt-1">
                An admin issued you a temporary password. Set a new one to continue using your account.
              </p>
            </div>
          </div>
        )}

        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
          {mustChange ? "Set Your New Password" : "Change Password"}
        </h1>
        <p className="text-[#A1A1AA] mb-8">
          {mustChange
            ? "Choose a strong password you'll remember."
            : "Enter your current password and a new one."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-form">
          {!mustChange && (
            <div>
              <Label className="text-[#A1A1AA] mb-2 block">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-12 bg-black/20 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#FFD700]/50"
                  required
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="text-[#A1A1AA] mb-2 block">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <Input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="pl-12 pr-12 h-12 bg-black/20 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#FFD700]/50"
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]"
              >
                {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[#A1A1AA] mb-2 block">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <Input
                type={showNew ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-type new password"
                className="pl-12 h-12 bg-black/20 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#FFD700]/50"
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full btn-gold py-4 text-lg rounded-lg"
            disabled={loading}
            data-testid="change-password-submit-btn"
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>

          {!mustChange && (
            <Button
              type="button"
              variant="ghost"
              className="w-full text-[#A1A1AA]"
              onClick={() => navigate(-1)}
              data-testid="change-password-cancel-btn"
            >
              Cancel
            </Button>
          )}

          {mustChange && (
            <Button
              type="button"
              variant="ghost"
              className="w-full text-[#52525B] hover:text-white"
              onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
              data-testid="change-password-signout-btn"
            >
              Sign out
            </Button>
          )}
        </form>
      </motion.div>
    </div>
  );
}
